import { useEffect, useCallback } from 'react';

/**
 * Hook pour gérer le badge de l'icône de l'application (PWA)
 * Affiche le nombre d'éléments en attente sur l'icône de l'app sur mobile/desktop
 * 
 * Compatible avec:
 * - Chrome/Edge sur Windows, macOS, Linux
 * - Safari sur macOS et iOS 16.4+
 * - Chrome sur Android
 */
export const useAppBadge = () => {
  // Check if the Badge API is supported
  const isSupported = 'setAppBadge' in navigator;

  // Set the badge count
  const setBadge = useCallback(async (count) => {
    // Try direct API first
    if (isSupported) {
      try {
        if (count > 0) {
          await navigator.setAppBadge(count);
        } else {
          await navigator.clearAppBadge();
        }
        return true;
      } catch (error) {
        console.error('Error setting app badge:', error);
      }
    }

    // Fallback: try to communicate with service worker
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({
          type: 'SET_BADGE',
          count: count
        });
        return true;
      } catch (error) {
        console.error('Error sending badge to SW:', error);
      }
    }

    return false;
  }, [isSupported]);

  // Clear the badge
  const clearBadge = useCallback(async () => {
    return setBadge(0);
  }, [setBadge]);

  return { setBadge, clearBadge, isSupported };
};

/**
 * Hook pour mettre à jour automatiquement le badge basé sur les compteurs
 * @param {number} totalCount - Le nombre total d'éléments à traiter
 */
export const useAppBadgeWithCount = (totalCount) => {
  const { setBadge, isSupported } = useAppBadge();

  useEffect(() => {
    setBadge(totalCount);
    
    // Also update document title with count for browser tabs
    const baseTitle = 'MSC Manager';
    if (totalCount > 0) {
      document.title = `(${totalCount}) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
    
    // Clear badge when component unmounts
    return () => {
      // Don't clear on unmount - keep showing badge
    };
  }, [totalCount, setBadge]);

  return { isSupported };
};

export default useAppBadge;
